/**
 * The lifetime "remove ads" license. There are no accounts: the key lives in this browser's
 * localStorage and PocketBase (server/pb_hooks/license.pb.js) says whether it is still good.
 * Polar sells the license and emails the key, which is how another browser gets unlocked.
 */
import { readLocal, writeLocal } from "@/lib/local-store";
import { pb } from "@/lib/pocketbase";

export const LICENSE_KEY = "qpp_license";
export const LICENSE_PRICE = "$9";

/** The browser asks PocketBase again after this long (a refund revokes the key). */
const RECHECK_MS = 24 * 60 * 60 * 1000;

export type StoredLicense = { key: string; checkedAt: number };

export function parseLicense(raw: string | null): StoredLicense | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<StoredLicense>;
    return typeof value.key === "string" && value.key
      ? { key: value.key, checkedAt: Number(value.checkedAt) || 0 }
      : null;
  } catch {
    return null;
  }
}

export function storedLicense(): StoredLicense | null {
  return parseLicense(readLocal(LICENSE_KEY));
}

export function storeLicense(key: string, checkedAt = Date.now()): void {
  writeLocal(LICENSE_KEY, JSON.stringify({ key, checkedAt }));
}

export function forgetLicense(): void {
  writeLocal(LICENSE_KEY, null);
}

export function needsRecheck(license: StoredLicense, now = Date.now()): boolean {
  return now - license.checkedAt > RECHECK_MS;
}

/** "QPP-…-AB12": enough to recognise a key without showing it in full. */
export function maskKey(key: string): string {
  return key.length <= 8 ? key : `${key.slice(0, 4)}…${key.slice(-4)}`;
}

/** Where to pay; the provider sends the buyer back to /license/success. */
export async function startCheckout(): Promise<string> {
  const { url } = await pb.send<{ url: string }>("/api/license/checkout", { method: "POST" });
  return url;
}

/** The key bought in a checkout, or null while the payment is still being processed. */
export async function claimLicense(checkoutId: string): Promise<string | null> {
  const result = await pb.send<{ key?: string }>("/api/license/claim", {
    method: "POST",
    body: { checkout_id: checkoutId },
  });
  return result.key ?? null;
}

export async function verifyLicense(key: string): Promise<boolean> {
  const { valid } = await pb.send<{ valid: boolean }>("/api/license/verify", {
    method: "POST",
    body: { key },
  });
  return valid;
}
